import { useCallback, useSyncExternalStore } from 'react'
import { isNil, isNonNil } from 'utils'

type Options<T> = {
  key: string
  defaultValue: T
}

const subscribers = new Map<string, Set<() => void>>()

// useSyncExternalStore needs a referentially stable snapshot, so only re-parse
// when the raw stored string changes — otherwise object values loop forever.
const snapshotCache = new Map<string, { raw: string; value: unknown }>()

export const subscribeToLocalStorageItem = (key: string, callback: () => void) => {
  const callbacks = subscribers.get(key) ?? new Set<() => void>()
  callbacks.add(callback)
  subscribers.set(key, callbacks)

  return () => {
    callbacks.delete(callback)
    if (callbacks.size === 0) subscribers.delete(key)
  }
}

const notify = (key: string) => {
  subscribers.get(key)?.forEach((callback) => callback())
}

export const readLocalStorageItem = <T>(key: string, defaultValue: T): T => {
  const raw = localStorage.getItem(key)
  if (isNil(raw)) return defaultValue

  const cached = snapshotCache.get(key)
  if (isNonNil(cached) && cached.raw === raw) return cached.value as T

  let value: T
  try {
    value = JSON.parse(raw) as T
  } catch {
    localStorage.removeItem(key)
    return defaultValue
  }
  snapshotCache.set(key, { raw, value })
  return value
}

// Comparing here lets callers write unconditionally. A caller that instead guards on the value it
// read back from storage is guarding on something other tabs can change, and two tabs holding
// different values will overwrite each other for as long as they are both open.
export const setLocalStorageItem = <T>(key: string, value: T) => {
  const raw = JSON.stringify(value)
  if (localStorage.getItem(key) === raw) return

  localStorage.setItem(key, raw)
  notify(key)
}

export const removeLocalStorageItem = (key: string) => {
  localStorage.removeItem(key)
  notify(key)
}

// The `storage` event only fires in other tabs, so forward it to keep web tabs in sync.
if (typeof window !== 'undefined') {
  window.addEventListener('storage', (event) => {
    if (isNonNil(event.key) && event.storageArea === localStorage) notify(event.key)
  })
}

export const useLocalStorage = <T>({ key, defaultValue }: Options<T>) => {
  const value = useSyncExternalStore(
    useCallback((callback) => subscribeToLocalStorageItem(key, callback), [key]),
    () => readLocalStorageItem(key, defaultValue)
  )

  const setValue = useCallback((next: T) => setLocalStorageItem(key, next), [key])

  return [value, setValue] as const
}
